import EmergencyNoticeForm from "@/components/notifications/EmergencyNoticeForm";
import NotificationPreferences from "@/components/notifications/NotificationPreferences";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { changePassword } from "@/api/settings";
import { readApiError } from "@/lib/issueLabels";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getPlatformPaymentInfo,
  updatePlatformPaymentInfo,
  type PlatformPaymentInfo,
} from "@/api/subscriptions";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
} from "@/components/ui/dialog";
import {
  Shield,
  LogOut,
  ArrowDownToLine,
  Info,
  MonitorSmartphone,
  HelpCircle,
  CreditCard,
  KeyRound,
  Settings,
  Globe,
} from "lucide-react";

/** Brand */
const PRIMARY = "#1F3A93";

/* ----------------------------- Types ---------------------------- */
type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

/* ------------------------ Reusable styles ----------------------- */
const sectionTitle = "text-[17px] font-semibold tracking-[-0.01em] text-gray-900";
const sectionDesc = "text-sm text-gray-600";

/** Brand (blue) button — white text */
function PrimaryButton(props: React.ComponentProps<typeof Button>) {
  const { className = "", ...rest } = props;
  return (
    <Button
      {...rest}
      className={[
        "px-5 h-10 rounded-full text-[15px] font-medium shadow-sm transition active:scale-[0.99]",
        "bg-[var(--brand)] text-white hover:opacity-90",
        "disabled:!bg-gray-200 disabled:!text-gray-700 disabled:cursor-not-allowed disabled:opacity-100",
        className,
      ].join(" ")}
      style={{ ["--brand" as any]: PRIMARY } as React.CSSProperties}
    />
  );
}

/** Quiet button — forced dark text & icons */
function QuietButton(props: React.ComponentProps<typeof Button>) {
  const { className = "", ...rest } = props;
  return (
    <Button
      {...rest}
      className={[
        "rounded-full h-10 px-4 text-[15px] font-medium",
        "bg-gray-100 ring-1 ring-black/5 border-0 shadow-sm",
        "!text-gray-900 hover:!text-gray-900",
        "hover:bg-gray-200 active:bg-gray-300",
        "disabled:bg-gray-100 disabled:!text-gray-500 disabled:ring-black/10 disabled:opacity-100",
        "[&_*]:!text-gray-900 [&_*]:!stroke-current",
        "whitespace-nowrap",
        className,
      ].join(" ")}
    />
  );
}

/** iOS-style switch row */

/** Lightweight callout */
function Callout({
  icon = <Info className="w-4 h-4" />,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 rounded-xl ring-1 ring-black/5 p-3.5 bg-gradient-to-b from-gray-50 to-white text-sm text-gray-700">
      <div className="mt-0.5 text-gray-600 shrink-0">{icon}</div>
      <div className="leading-relaxed min-w-0 break-words">{children}</div>
    </div>
  );
}

/* ------------------------------- Page ------------------------------- */
export default function SuperAdminSettingsPage() {
  const { t } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();


  const passwordForm = useForm<PasswordForm>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  async function onChangePassword(data: PasswordForm) {
    if (data.newPassword !== data.confirmPassword) {
      toast.error(t("superAdmin.settings.security.passwordMismatch"));
      return;
    }
    try {
      await changePassword(data.currentPassword, data.newPassword);
      passwordForm.reset();
      await logout();
      navigate("/login", { replace: true });
      toast.success(t("profile.passwordSignInAgain"));
    } catch (error) {
      toast.error(readApiError(error, t("common.error")));
    }
  }

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  /* ── Detalles de Pago de la Plataforma ── */
  const paymentDetailsQuery = useQuery({
    queryKey: ["platform", "payment-details"],
    queryFn: getPlatformPaymentInfo,
    staleTime: 5 * 60 * 1000,
  });

  const emptyPaymentDetails: Omit<PlatformPaymentInfo, "updated_at"> = {
    bank_name: "", bank_account_name: "", bank_account_number: "",
    bank_routing_number: "", bank_notes: "",
    zelle_phone: "", zelle_email: "", zelle_name: "",
    paypal_email: "", payment_instructions: "",
    pago_movil_bank: "", pago_movil_phone: "", pago_movil_id_number: "", cash_instructions: "",
  };

  const [pdForm, setPdForm] = React.useState<Omit<PlatformPaymentInfo, "updated_at">>(emptyPaymentDetails);
  const [pdLoaded, setPdLoaded] = React.useState(false);

  React.useEffect(() => {
    if (paymentDetailsQuery.data && !pdLoaded) {
      const { updated_at, ...rest } = paymentDetailsQuery.data;
      setPdForm(rest);
      setPdLoaded(true);
    }
  }, [paymentDetailsQuery.data, pdLoaded]);

  const updatePdMut = useMutation({
    mutationFn: (payload: Partial<Omit<PlatformPaymentInfo, "updated_at">>) =>
      updatePlatformPaymentInfo(payload),
    onSuccess: (data) => {
      qc.setQueryData(["platform", "payment-details"], data);
      toast.success(t("settings.paymentDetailsUpdated"));
    },
    onError: (err: any) => {
      console.error("Failed to save platform payment details", err);
      toast.error(t("settings.paymentDetailsUpdateError"));
    },
  });

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-3 sm:px-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-2xl ring-1 ring-black/10 p-2.5 bg-white shadow-sm">
          <Settings className="w-5 h-5 text-gray-700" />
        </div>
        <div>
          <h2 className="text-[20px] font-semibold tracking-[-0.01em]">
            {t("superAdmin.settings.title")}
          </h2>
          <p className="text-gray-600 text-sm">
            {t("superAdmin.settings.subtitle")}
          </p>
        </div>
      </div>

      <NotificationPreferences />
      <EmergencyNoticeForm />

      {/* Language Settings */}
      <Card className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <Globe className="h-4 w-4" />
            {t("profile.language")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.languageDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-4 sm:px-6 pb-5 sm:pb-6">
          <LanguageSelector />
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <Shield className="w-4 h-4" /> {t("profile.security")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("superAdmin.settings.security.desc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 sm:px-6 pb-5 sm:pb-6">
          <form
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            onSubmit={passwordForm.handleSubmit(onChangePassword)}
          >
            <div className="min-w-0">
              <Label htmlFor="currentPassword" className="text-[15px]">
                {t("profile.currentPassword")}
              </Label>
              <Input
                id="currentPassword"
                type="password"
                className="w-full"
                {...passwordForm.register("currentPassword")}
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="newPassword" className="text-[15px]">
                {t("profile.newPassword")}
              </Label>
              <Input
                id="newPassword"
                type="password"
                className="w-full"
                {...passwordForm.register("newPassword")}
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="confirmPassword" className="text-[15px]">
                {t("profile.confirmPassword")}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                className="w-full"
                {...passwordForm.register("confirmPassword")}
              />
            </div>

            <div className="lg:col-span-3 sm:col-span-2 col-span-1 flex flex-wrap items-center justify-end gap-2">
              <QuietButton type="button" onClick={onLogout}>
                <LogOut className="w-4 h-4" />
                {t("common.logout")}
              </QuietButton>
              <PrimaryButton type="submit" disabled={passwordForm.formState.isSubmitting} className="gap-2">
                <KeyRound className="w-4 h-4" />
                {t("profile.changePassword")}
              </PrimaryButton>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Detalles de Pago de la Plataforma */}
      <Card className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <CreditCard className="w-4 h-4" /> {t("superAdmin.settings.paymentDetails.title")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("superAdmin.settings.paymentDetails.desc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 px-4 sm:px-6 pb-5 sm:pb-6">
          {paymentDetailsQuery.isLoading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-9 rounded-lg bg-gray-100 animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* General Instructions */}
              <div>
                <Label className="text-[15px]">{t("superAdmin.settings.paymentDetails.generalInstructions")}</Label>
                <p className="text-xs text-gray-500 mb-1">{t("superAdmin.settings.paymentDetails.generalInstructionsHint")}</p>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                  placeholder={t("superAdmin.settings.paymentDetails.generalInstructionsPlaceholder")}
                  value={pdForm.payment_instructions}
                  onChange={e => setPdForm(f => ({ ...f, payment_instructions: e.target.value }))}
                />
              </div>

              {/* Transferencia Bancaria */}
              <div className="space-y-2">
                <p className="text-[13px] font-semibold text-gray-700 uppercase tracking-wider">{t("settings.paymentMethod.bankTransfer")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[15px]">{t("settings.bankName")}</Label>
                    <Input value={pdForm.bank_name} onChange={e => setPdForm(f => ({ ...f, bank_name: e.target.value }))} placeholder={t("settings.exBanesco")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.accountHolderName")}</Label>
                    <Input value={pdForm.bank_account_name} onChange={e => setPdForm(f => ({ ...f, bank_account_name: e.target.value }))} placeholder={t("settings.accountHolderPlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.accountNumber")}</Label>
                    <Input value={pdForm.bank_account_number} onChange={e => setPdForm(f => ({ ...f, bank_account_number: e.target.value }))} placeholder={t("settings.accountNumberPlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.routingNumber")}</Label>
                    <Input value={pdForm.bank_routing_number} onChange={e => setPdForm(f => ({ ...f, bank_routing_number: e.target.value }))} placeholder={t("settings.routingPlaceholder")} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[15px]">{t("superAdmin.settings.paymentDetails.bankNotes")}</Label>
                    <Input value={pdForm.bank_notes} onChange={e => setPdForm(f => ({ ...f, bank_notes: e.target.value }))} placeholder={t("superAdmin.settings.paymentDetails.bankNotesPlaceholder")} />
                  </div>
                </div>
              </div>

              {/* Zelle */}
              <div className="space-y-2">
                <p className="text-[13px] font-semibold text-gray-700 uppercase tracking-wider">{t("settings.paymentMethod.zelle")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[15px]">{t("superAdmin.settings.paymentDetails.zelleName")}</Label>
                    <Input value={pdForm.zelle_name} onChange={e => setPdForm(f => ({ ...f, zelle_name: e.target.value }))} placeholder={t("superAdmin.settings.paymentDetails.zelleNamePlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.zellePhone")}</Label>
                    <Input value={pdForm.zelle_phone} onChange={e => setPdForm(f => ({ ...f, zelle_phone: e.target.value }))} placeholder={t("superAdmin.settings.paymentDetails.zellePhonePlaceholder")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.zelleEmail")}</Label>
                    <Input type="email" value={pdForm.zelle_email} onChange={e => setPdForm(f => ({ ...f, zelle_email: e.target.value }))} placeholder={t("settings.zelleEmailPlaceholder")} />
                  </div>
                </div>
              </div>

              {/* Pago Movil */}
              <div className="space-y-2">
                <p className="text-[13px] font-semibold text-gray-700 uppercase tracking-wider">{t("settings.paymentMethod.pagoMovil")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[15px]">{t("settings.bank")}</Label>
                    <Input value={pdForm.pago_movil_bank} onChange={e => setPdForm(f => ({ ...f, pago_movil_bank: e.target.value }))} placeholder={t("settings.exMercantil")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.idNumber")}</Label>
                    <Input value={pdForm.pago_movil_id_number} onChange={e => setPdForm(f => ({ ...f, pago_movil_id_number: e.target.value }))} placeholder={t("settings.exIdNumber")} />
                  </div>
                  <div>
                    <Label className="text-[15px]">{t("settings.phone")}</Label>
                    <Input value={pdForm.pago_movil_phone} onChange={e => setPdForm(f => ({ ...f, pago_movil_phone: e.target.value }))} placeholder={t("superAdmin.settings.paymentDetails.pagoMovilPhonePlaceholder")} />
                  </div>
                </div>
              </div>

              {/* PayPal */}
              <div className="space-y-2">
                <p className="text-[13px] font-semibold text-gray-700 uppercase tracking-wider">{t("settings.paymentMethod.paypal")}</p>
                <div>
                  <Label className="text-[15px]">{t("settings.paypalEmail")}</Label>
                  <Input type="email" value={pdForm.paypal_email} onChange={e => setPdForm(f => ({ ...f, paypal_email: e.target.value }))} placeholder={t("settings.paypalEmailPlaceholder")} />
                </div>
              </div>

              <div className="flex justify-end">
                <PrimaryButton
                  disabled={updatePdMut.isPending}
                  onClick={() => updatePdMut.mutate(pdForm)}
                  className="gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  {updatePdMut.isPending ? t("settings.saving") : t("settings.updatePaymentDetails")}
                </PrimaryButton>
              </div>

              {paymentDetailsQuery.data?.updated_at && (
                <p className="text-xs text-gray-400 text-right">
                  {t("superAdmin.settings.paymentDetails.lastUpdated")} {new Date(paymentDetailsQuery.data.updated_at).toLocaleString()}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Install App */}
      <Card className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <ArrowDownToLine className="w-4 h-4" />
            {t("profile.installApp")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("superAdmin.settings.install.desc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 sm:px-6 pb-5 sm:pb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <InstallPWAButton big />
            <QuietButton
              onClick={() => {
                const el = document.getElementById("install-help");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <HelpCircle className="w-4 h-4" />
              {t("superAdmin.settings.install.help")}
            </QuietButton>
          </div>

          <Callout icon={<MonitorSmartphone className="w-4 h-4" />}>
            {t("superAdmin.settings.install.callout")}
          </Callout>

          <div
            id="install-help"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
          >
            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">
                {t("superAdmin.settings.install.iosTitle")}
              </h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("superAdmin.settings.install.iosStep1")}</li>
                <li>{t("superAdmin.settings.install.iosStep2")}</li>
                <li>{t("superAdmin.settings.install.iosStep3")}</li>
              </ol>
            </div>

            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">
                {t("superAdmin.settings.install.androidTitle")}
              </h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("superAdmin.settings.install.androidStep1")}</li>
                <li>{t("superAdmin.settings.install.androidStep2")}</li>
                <li>{t("superAdmin.settings.install.androidStep3")}</li>
              </ol>
            </div>

            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">
                {t("superAdmin.settings.install.desktopTitle")}
              </h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("superAdmin.settings.install.desktopStep1")}</li>
                <li>{t("superAdmin.settings.install.desktopStep2")}</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- Language Selector (matches resident UI) ---------- */
function LanguageSelector() {
  const { language, setLanguage, t } = useLanguage();

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setLanguage("en")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "en"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20",
          ].join(" ")}
        >
          <span className="text-2xl">🇺🇸</span>
          <div className="text-left">
            <div
              className={[
                "font-medium",
                language === "en" ? "text-[#1F3A93]" : "text-gray-900",
              ].join(" ")}
            >
              {t("profile.english")}
            </div>
            <div className="text-xs text-gray-500">{t("profile.default")}</div>
          </div>
          {language === "en" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
        </button>

        <button
          type="button"
          onClick={() => setLanguage("es")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "es"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20",
          ].join(" ")}
        >
          <span className="text-2xl">🇪🇸</span>
          <div className="text-left">
            <div
              className={[
                "font-medium",
                language === "es" ? "text-[#1F3A93]" : "text-gray-900",
              ].join(" ")}
            >
              {t("profile.spanish")}
            </div>
            <div className="text-xs text-gray-500">{t("profile.spanish")}</div>
          </div>
          {language === "es" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        {t("profile.languageAppliedNote")}
      </p>
    </div>
  );
}

/* ----------------------- Install controls ----------------------- */

function InstallPWAButton({ big = false }: { big?: boolean }) {
  const { available, promptToInstall, installedOrUnsupported, displayMode } =
    useInstallPrompt();
  const { t } = useLanguage();
  const label = available
    ? t("superAdmin.settings.install.installApp")
    : installedOrUnsupported
    ? t("superAdmin.settings.install.installedOrUnsupported")
    : t("superAdmin.settings.install.install");

  return (
    <PrimaryButton
      onClick={promptToInstall}
      disabled={!available}
      className={big ? "gap-2 h-11 px-5 text-[16px]" : "gap-2"}
      title={
        installedOrUnsupported
          ? displayMode === "standalone"
            ? t("superAdmin.settings.install.alreadyInstalled")
            : t("superAdmin.settings.install.notSupported")
          : t("superAdmin.settings.install.installApp")
      }
    >
      <ArrowDownToLine className="w-5 h-5" />
      {label}
    </PrimaryButton>
  );
}

function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
  const [available, setAvailable] = React.useState(false);
  const [displayMode, setDisplayMode] =
    React.useState<"browser" | "standalone" | "twa">("browser");

  React.useEffect(() => {
    const isStandalone =
      (window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches) ||
      // @ts-ignore
      (window.navigator as any).standalone === true;
    setDisplayMode(isStandalone ? "standalone" : "browser");
  }, []);

  React.useEffect(() => {
    function onBeforeInstallPrompt(e: any) {
      e.preventDefault();
      setDeferredPrompt(e);
      setAvailable(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt as any);
    return () =>
      window.removeEventListener(
        "beforeinstallprompt",
        onBeforeInstallPrompt as any
      );
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setAvailable(!mq.matches && !!deferredPrompt);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [deferredPrompt]);

  async function promptToInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") {
      setAvailable(false);
    }
  }

  const installedOrUnsupported = displayMode === "standalone" || !available;
  return { available, promptToInstall, installedOrUnsupported, displayMode };
}
